import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8773p14a1h3mr6f1g";
const MESSAGE_LIMIT = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      setMessage("");
      return;
    }

    formData.delete("website_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        formEl.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试。");
    }
  };

  return (
    <form
      id="baoliao-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="投票问题"
          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="投票问题">投票与打分问题</option>
          <option value="违规举报">违规或刷分举报</option>
          <option value="内容纠错">内容纠错</option>
          <option value="合作咨询">社区与活动合作</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
          详细内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={MESSAGE_LIMIT}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="请描述你遇到的问题或建议，我们会尽快查看"
          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
        />
        <p className="mt-1.5 text-right text-xs text-foreground-400">
          {message.length} / {MESSAGE_LIMIT}
        </p>
      </div>

      <div className="alt-contact-field" aria-hidden="true">
        <label htmlFor="contact-website-alt">请勿填写此项</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "success" && (
        <p className="mt-5 flex items-center gap-2 px-4 py-3 rounded-md bg-secondary-50 text-secondary-800 text-sm">
          <i className="ri-checkbox-circle-line text-lg" />
          已收到你的反馈，我们会在核对后通过邮箱与你联系。
        </p>
      )}
      {status === "error" && (
        <p className="mt-5 flex items-center gap-2 px-4 py-3 rounded-md bg-primary-50 text-primary-800 text-sm">
          <i className="ri-error-warning-line text-lg" />
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 w-full sm:w-auto px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? "提交中…" : "提交反馈"}
      </button>
    </form>
  );
}